import {
  Dialog,
  Image,
  ScrollView,
  SmartLink,
  Stack,
  XStack,
  YStack,
  colors,
  isWeb,
  shadowStandard,
} from '@my/ui'
import React, { ReactElement, useEffect } from 'react'
import { Lock, X } from '@tamagui/lucide-icons'
import { VerifyDialogPage } from './verify-dialog-page'
import { RegisterDialogPage } from './register-dialog-page'
import { RedeemedDialogPage } from './redeemed-dialog-page'
import { ErrorDialogPage } from './error-dialog-page'
import { RedeemOutput } from '../../services/redeem'
import { BackHandler } from 'react-native'
import { SolitoImage } from 'solito/image'
import { useIFrame } from 'app/provider/iframe'

export enum DialogState {
  Closed,
  OpenLogin,
  OpenVerify,
  OpenRedeem,
}

interface DialogProps {
  onOpenChange: (value) => void
  state: DialogState
  offer: {
    id: string
    name: string
    description?: string
    featuredImage?: {
      url: string
    }
    brand: {
      slug: string
      logo?: {
        url: string
      }
    }
    usesCodes: boolean
    canRedeem: boolean
    embedUrl?: string
    offerLinkLabel?: string
  }
  redemption: RedeemOutput | undefined
  error: string | undefined
}

export const RedeemDialog = ({ onOpenChange, state, offer, redemption, error }: DialogProps) => {
  let title = ''
  let description = ''
  let body: ReactElement | null = null
  let showLockIcon = false

  const { isIframe, microsite } = useIFrame()

  const embedUrl = microsite?.embedUrl || offer.embedUrl

  if (state === DialogState.OpenLogin) {
    title = 'To gain access, join Student Saviour'
    description = 'Get the best student discounts, freebies & giveaways!'
    showLockIcon = true
    body = <RegisterDialogPage embedUrl={embedUrl} isEmbed={isIframe} />
  } else if (state === DialogState.OpenVerify) {
    title = 'To gain access, please verify your student email'
    description = 'Get the best student discounts, freebies & giveaways!'
    showLockIcon = true
    body = <VerifyDialogPage />
  } else if (state === DialogState.OpenRedeem && redemption && !error) {
    title = offer.name
    description = offer.description || ''
    body = <RedeemedDialogPage redemption={redemption} />
  } else if (state === DialogState.OpenRedeem && error) {
    title = offer.name
    description = offer.description || ''
    body = <ErrorDialogPage error={error} redemption={redemption} />
  }

  useEffect(() => {
    const backAction = () => {
      onOpenChange(false)
      console.log('backAction', state)
      return state !== DialogState.Closed
    }

    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction)

    return () => backHandler.remove()
  }, [state])

  return (
    <Dialog modal open={state !== DialogState.Closed} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          onPress={onOpenChange}
          key="overlay"
          animation="quick"
          bg="$overlay8"
          opacity={1}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />

        <Dialog.Content
          elevate
          width="90%"
          maxWidth={isWeb ? '600px' : 'auto'}
          key="content"
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          px={0}
          py={0}
        >
          <Dialog.Close>
            <Stack
              tag="button"
              onPress={onOpenChange}
              position="absolute"
              right={0}
              top={-30}
              cursor="pointer"
            >
              <X color="white" size={30} fill={'white'} stroke={'white'} />
            </Stack>
          </Dialog.Close>
          <ScrollView>
            <Stack backgroundColor="$offWhite" overflow="hidden" space borderRadius="$5">
              <YStack justifyContent="center" alignItems="center" px={isWeb ? '$0' : '$5'} pb="$6">
                {!showLockIcon && offer.featuredImage && (
                  <Image
                    aspectRatio={358 / 100}
                    source={{
                      uri: offer.featuredImage.url,
                    }}
                    minWidth="100%"
                    minHeight={120}
                  />
                )}
                {!showLockIcon && offer.brand.logo && (
                  <SmartLink
                    Component={Stack}
                    href={`/discounts/${offer.brand.slug}`}
                    position="absolute"
                    aspectRatio={1}
                    top={20}
                    m="auto"
                    bg="$white"
                    boc="$white"
                    bw="$2"
                    br="$3"
                    w={88}
                    h={88}
                    zIndex="$1"
                    $gtSm={{ top: 40 }}
                    hoverStyle={{ opacity: 0.95 }}
                    pressStyle={{ opacity: 0.85 }}
                    focusStyle={{ opacity: 0.8 }}
                    {...shadowStandard}
                  >
                    <SolitoImage
                      src={offer.brand.logo.url}
                      alt=""
                      contentFit="contain"
                      fill
                      sizes="88px"
                    />
                  </SmartLink>
                )}

                {showLockIcon && (
                  <YStack
                    mt="$6"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius={1000}
                    w={60}
                    h={60}
                    backgroundColor="white"
                    {...shadowStandard}
                  >
                    <Lock color={colors.primary} />
                  </YStack>
                )}

                <Dialog.Title pt="$4" textAlign="center" color="$black" size="$7" fow="600">
                  {title}
                </Dialog.Title>
                {description && (
                  <Dialog.Description
                    pt="$2"
                    py="$3"
                    textAlign="center"
                    color="$overlay11"
                    fow="600"
                  >
                    {description}
                  </Dialog.Description>
                )}
                {body}
              </YStack>
            </Stack>
          </ScrollView>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
